/**
 * useAppForm — the canonical react-hook-form setup for the app.
 *
 * Bundles the conventions every form should share so pages don't re-decide
 * them ad hoc:
 *   - Zod schema validation via zodResolver
 *   - `mode: "onTouched"` — validate on blur, then live after first touch
 *     (feedback before submit, without nagging on first keystroke)
 *   - `shouldFocusError: true` — focus the first invalid field on submit
 *
 * Use together with <FormErrorSummary> and applyApiFieldErrors for a complete,
 * accessible form experience.
 */

import { useForm } from "react-hook-form";
import type { FieldValues, UseFormProps, UseFormReturn } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import type { ZodType } from "zod";

export interface UseAppFormProps<T extends FieldValues>
  extends Omit<UseFormProps<T>, "resolver"> {
  /**
   * Zod schema whose input and output both match the form value type.
   * Use useAppForm for schemas WITHOUT defaults/transforms (input === output);
   * forms whose schema reshapes values (defaults, coerce) should call useForm
   * directly with `mode: "onTouched"` + `shouldFocusError: true`.
   */
  schema: ZodType<T, T>;
}

export function useAppForm<T extends FieldValues>({
  schema,
  mode = "onTouched",
  ...rest
}: UseAppFormProps<T>): UseFormReturn<T> {
  return useForm<T>({
    resolver: zodResolver(schema),
    mode,
    shouldFocusError: true,
    ...rest,
  });
}
